import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ProjectDocumentRevision } from '../../api/project-document-revisions';
import {
  UpdateProject,
  createProject,
  createPlugAndPlayProject,
  getAllMyTaskProjects,
  getCountDocumentRevisions,
  getDocumentRevisionStatuses,
  getDocumentRevisions,
  getProject,
  getProjects,
  getSumProjectProgresses,
  updateProject
} from '../../api/projects';
import { ProjectAccessControlUser } from '../ProjectAccessControlUser/projectAccessControlUserSlice';
import { ProjectDocument } from '../ProjectDocument/projectDocumentSlice';
import { DocumentWorkflowWithStepAndApproval } from '../ProjectDocumentWorkflow/projectDocumentWorkflowSlice';

export const getProjectsAsync = createAsyncThunk(
  'projects/getProjects',
  getProjects
);

export const getAllMyTaskProjectsAsync = createAsyncThunk(
  'projects/getAllMyTaskProjects',
  getAllMyTaskProjects
);

export const getProjectAsync = createAsyncThunk(
  'projects/getProject',
  getProject
);

export const createProjectAsync = createAsyncThunk(
  'projects/createProject',
  createProject
);

export const createPlugAndPlayProjectAsync = createAsyncThunk(
  'projects/createPlugAndPlayProject',
  createPlugAndPlayProject
);

export const updateProjectAsync = createAsyncThunk(
  'projects/updateProject',
  async (projectData: UpdateProject, thunkAPI) => {
    const project = await updateProject(projectData);
    thunkAPI.dispatch(getProjectAsync(project.id));
    return project;
  }
);

export const getDocumentRevisionStatusesAsync = createAsyncThunk(
  'projects/getDocumentRevisionStatuses',
  getDocumentRevisionStatuses
);

export const getDocumentRevisionsAsync = createAsyncThunk(
  'projects/getDocumentRevisions',
  getDocumentRevisions
);

export const getCountDocumentRevisionsByIssueDateAsync = createAsyncThunk(
  'projects/getCountDocumentRevisionsByIssueDate',
  getCountDocumentRevisions
);

export const getCountDocumentRevisionsByCompletedDateAsync = createAsyncThunk(
  'projects/getCountDocumentRevisionsByCompleted',
  getCountDocumentRevisions
);

export const getSumProjectProgressesAsync = createAsyncThunk(
  'projects/getSumProjectProgresses',
  getSumProjectProgresses
);

export interface Project {
  id: number;
  projectRegisterId: number;
  projectNo?: string;
  name: string;
  plantName: string;
  customerCompanyName: string;
  productName: string;
  businessUnitName: string;
  businessUnitGroupName: string;
  projectStatus: string;
  users: ProjectAccessControlUser[];
}

export interface DocumentRevisionCounts {
  month: string;
  plan: number;
  actual: number;
}

export interface CountStatus {
  waitForIssue: {
    count: number;
    onTime: number;
    overdue: number;
  };
  inProgress: {
    count: number;
    onTime: number;
    overdue: number;
  };
  done: {
    count: number;
    completed: number;
    completedDelayIssue: number;
    rejected: number;
    canceled: number;
  };
}

export interface DocumentRevisionDashboard extends ProjectDocumentRevision {
  id: number;
  projectDocument: ProjectDocument;
  currentProjectDocumentWorkflowId: number | null;
  currentProjectDocumentWorkflow: DocumentWorkflowWithStepAndApproval | null;
  actualIssueDate: Date | null;
}

export interface SumProjectProgress {
  sumTotalManHours: number;
  data: DocumentRevisionCounts[];
}

interface ProjectState {
  data: Project[];
  count: number;
  allMyTaskProjects: Project[];
  selectedProject: Project | null;
  documentRevisionStatuses: CountStatus | null;
  documentRevisions: { count: number; data: DocumentRevisionDashboard[] };
  countDocumentRevisionByIssueDate: DocumentRevisionCounts[];
  countDocumentRevisionByCompletedDate: DocumentRevisionCounts[];
  sumProjectProgress: SumProjectProgress;
}

const initialState: ProjectState = {
  data: [],
  count: 0,
  allMyTaskProjects: [],
  selectedProject: null,
  documentRevisionStatuses: null,
  documentRevisions: { count: 0, data: [] },
  countDocumentRevisionByIssueDate: [],
  countDocumentRevisionByCompletedDate: [],
  sumProjectProgress: { sumTotalManHours: 0, data: [] }
};

export default createSlice({
  name: 'projects',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getProjectsAsync.fulfilled, (state, action) => {
      state.data = action.payload.data;
      state.count = action.payload.count;
    });

    builder.addCase(getAllMyTaskProjectsAsync.fulfilled, (state, action) => {
      state.allMyTaskProjects = action.payload;
    });

    builder.addCase(getProjectAsync.fulfilled, (state, action) => {
      state.selectedProject = action.payload;
    });

    builder.addCase(
      getDocumentRevisionStatusesAsync.fulfilled,
      (state, action) => {
        state.documentRevisionStatuses = action.payload.data;
      }
    );

    builder.addCase(getDocumentRevisionsAsync.fulfilled, (state, action) => {
      state.documentRevisions = action.payload;
    });

    builder.addCase(
      getCountDocumentRevisionsByIssueDateAsync.fulfilled,
      (state, action) => {
        state.countDocumentRevisionByIssueDate = action.payload.data;
      }
    );

    builder.addCase(
      getCountDocumentRevisionsByCompletedDateAsync.fulfilled,
      (state, action) => {
        state.countDocumentRevisionByCompletedDate = action.payload.data;
      }
    );

    builder.addCase(getSumProjectProgressesAsync.fulfilled, (state, action) => {
      state.sumProjectProgress = action.payload;
    });
  }
});
